import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Tag, Spin, Alert, Tooltip, Dropdown } from "antd";
import { CalendarOutlined, ClockCircleOutlined, CopyOutlined, EllipsisOutlined } from "@ant-design/icons";
import AppointmentService from "../../services/appointmentService";
import UpcomingAppointmentDate from "../FormatDate/UpcomingAppointmentDate";
// import FormatDateTime from "../FormatDate/FormatDateTime";
import Notification from "../Notification/Notification";
import { AppointmentStatus } from "../GlobalComponents/StatusLabels";
import "./PatientAppointmentsModal.css";
import FormatDateTime from "../FormatDate/FormatDateTime";
import { formatDate, formatTime } from "../../utils/DateFormat";
import { useStateGlobalContext } from "../../contexts/GlobalContext";
import { doPatch, doDelete } from "../../API/apis";
import showConfirmModal from "../ModalConfirm/ModalConfirm";

const items = [
  {
    key: 1,
    label: "Scheduled",
  },
  {
    key: 5,
    label: "Appt. Sent",
  },
  {
    key: 2,
    label: "Confirmed"
  },
  {
    key: 3,
    label: "Rescheduled",
  },
  {
    key: 4,
    label: "Cancelled",
  }
];

const PatientAppointmentsModal = ({ visible, onClose, patientId, patientName }) => {
  const { officeSetting, getAppointments } = useStateGlobalContext();
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);

  const officeDateFormat = officeSetting && officeSetting?.date_format;
  const officeTimeFormat = officeSetting && officeSetting?.time_format;
  const officeTimezone = officeSetting && officeSetting?.facility_time_zone;

  useEffect(() => {
    if (visible && patientId) {
      fetchAppointments();
    }
  }, [visible, patientId]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      // Fetch all appointments
      const allAppointmentsResponse = await AppointmentService.getPatientAppointments(patientId);

      if (allAppointmentsResponse.status === 200) {
        setAppointments(allAppointmentsResponse.data || []);
      } else {
        Notification.error("Failed to fetch appointments");
        setAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      Notification.error("Error loading appointments");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (record) => {
    const dateStr = formatDate(record.appointment_date, officeDateFormat, officeTimezone);
    const timeStr = formatTime(record.appointment_date, officeTimeFormat, officeTimezone);
    const text = `Hi, ${patientName} your upcoming appointment is on ${dateStr} at ${timeStr}`;

    navigator.clipboard.writeText(text).then(() => {
      Notification.success("Appointment details copied to clipboard");
    }).catch((err) => {
      console.error("Failed to copy:", err);
      Notification.error("Failed to copy details");
    });
  };

  const handleStatus = async (record, key) => {
    const statusMap = {
      1: "Scheduled",
      2: "Confirmed",
      3: "Rescheduled",
      4: "Cancelled",
      5: "Appt. Sent",
    };

    const data = { current_status: statusMap[key] || "No Show" };

    try {
      const response = await doPatch(`/appointments/update/${record.id}/`, data);
      if (response.status === 200) {
        Notification.success("Status updated successfully");
        fetchAppointments();
        if (getAppointments) getAppointments();
      } else {
        Notification.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Notification.error("An error occurred while updating status");
    }
  };

  const columns = [
    {
      title: "Date & Time",
      dataIndex: "appointment_date",
      key: "appointment_date",
      render: (date, record) => (
        <div className="appointment-datetime">
          <div className="appointment-date">
            <CalendarOutlined className="mr-1" />
            <UpcomingAppointmentDate date={date} />
          </div>
          <div className="appointment-time">
            <ClockCircleOutlined className="mr-1" />
            <FormatDateTime date={record.appointment_date} type="time" />
          </div>
        </div>
      ),
      width: "20%",
    },
    {
      title: "Provider",
      dataIndex: "appointment_with",
      key: "appointment_with",
      render: (provider) => (
        <div className="">
          {provider || "Not assigned"}
        </div>
      ),
      width: "20%",
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      render: (service) => service || "Not specified",
      width: "20%",
    },
    {
      title: "Reason",
      dataIndex: "visit_reason",
      key: "visit_reason",
      render: (reason) => (
        <div className="appointment-reason">
          {reason || "No reason provided"}
        </div>
      ),
      width: "25%",
    },
    {
      title: "Status",
      dataIndex: "current_status",
      key: "current_status",
      render: (status) => <AppointmentStatus status={status} />,
      width: "15%",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Copy Appointment Message">
            <Button
              type="text"
              icon={<CopyOutlined style={{ color: '#1890ff' }} />}
              onClick={() => handleCopy(record)}
              className="copy-appointment-btn"
            />
          </Tooltip>
          <Dropdown
            menu={{
              items: items.map((item) => ({
                ...item,
                onClick: () => {
                  if ([1, 2, 3, 4, 5].includes(item.key)) {
                    handleStatus(record, item.key);
                  }
                },
              })),
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
      width: "15%",
    },
  ];

  return (
    <Modal
      title={
        <div className="modal-title">
          <CalendarOutlined className="mr-2" />
          Upcoming Appointments for {patientName}
        </div>
      }
      closeIcon={
        <Button size="small" className="app-close-icon" shape="round">
          Close
        </Button>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      className="patient-appointments-modal"
    >
      <div className="appointments-content">
        {loading ? (
          <div className="loading-container">
            <Spin size="small" />
            <p className="mt-4">Loading appointments...</p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={appointments}
            rowKey={(record) => record.id || record.appointment_date}
            pagination={false}
            scroll={{ x: 700, y: 600 }}
            locale={{
              emptyText: "No appointments found for this patient"
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default PatientAppointmentsModal;
